import { BudgetAllowance } from '@frontend/budget/types';
import { Certification } from '@frontend/certificate/types';
import { Feature } from '@frontend/functionality/types';
import { IGroup } from '@frontend/group/types';
import { IProduct, ProductType } from '@frontend/product/types';
import { Stock } from '@frontend/stock/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useState } from 'react';

import { canOrderProduct } from '../../utils/product-order-permission';
import { useScreen } from '../useScreen';

export const useSelectAttributes = () => {
    const { updateWizardData, store, onSubmit, onBack, navigationState, wizardData } = useScreen();
    const isRecycleMode = wizardData.browseProductsContext?.feature === Feature.RECYCLE_TRANSACTION;
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [selectedProductStocks, setSelectedProductStocks] = useState<Stock[]>([]);
    const [selectedAmount, setSelectedAmount] = useState<number>(1);
    const [selectedAllowance, setSelectedAllowance] = useState<BudgetAllowance | null>(null);
    const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);
    const [canOrderSelectedProduct, setCanOrderSelectedProduct] = useState(false);
    const [isCheckingOrderPermission, setIsCheckingOrderPermission] = useState(false);

    const category: IGroup | null = store?.data?.selectedLeafCategory ?? null;
    const categoryStack: IGroup[] = store?.data?.browseProductsNavigation?.categoryStack ?? [];
    const orderFallbackStep = store?.steps?.find((step) => step.screen === ScreenType.SELECT_ATTRIBUTES && step.trigger === ScreenTrigger.SUBMIT_FALSE);

    // Get the parent category stack (without the leaf category) for navigating back
    const parentCategoryStack = categoryStack.length > 0 ? categoryStack.slice(0, -1) : [];

    const handleProductSelected = (
        product: IProduct | null,
        stocks: Stock[],
        amount: number,
        allowance: BudgetAllowance | null,
        certification: Certification | null,
        canOrder: boolean
    ) => {
        setSelectedProduct(product);
        setSelectedProductStocks(stocks);
        setSelectedAmount(amount);
        setSelectedAllowance(allowance);
        setSelectedCertification(certification);
        setCanOrderSelectedProduct(canOrder);
    };

    const navigateBack = () => {
        // Update wizard data to restore the parent category stack (without the leaf category)
        updateWizardData({
            selectedLeafCategory: null,
            browseProductsNavigation: {
                categoryStack: parentCategoryStack,
                leafSelectedProduct: null,
                selectedProductStocks: []
            },
            selectedCertification: null
        });
        onBack();
    };

    const confirmProductSelection = async () => {
        const shouldOrder = selectedProduct?.type === ProductType.CONSUMABLE && selectedProductStocks.length === 0 && canOrderSelectedProduct;
        if (shouldOrder) {
            if (!navigationState.user) return;
            if (!orderFallbackStep) return;
            setIsCheckingOrderPermission(true);
            const canOrder = await canOrderProduct(navigationState.user.accountId, selectedProduct.id, navigationState.user.auth);
            setIsCheckingOrderPermission(false);
            if (!canOrder) return;
            updateWizardData({
                selectedLeafCategory: category,
                browseProductsNavigation: {
                    categoryStack,
                    leafSelectedProduct: selectedProduct,
                    selectedProductStocks
                },
                selectedProduct,
                selectedProductStocks,
                selectedAmount,
                selectedAllowance,
                selectedCertification
            });
            onSubmit(orderFallbackStep);
            return;
        }
        if (selectedProduct) {
            updateWizardData({
                browseProductsNavigation: {
                    categoryStack,
                    leafSelectedProduct: selectedProduct,
                    selectedProductStocks
                },
                selectedProduct,
                selectedProductStocks,
                selectedAmount,
                selectedAllowance,
                selectedCertification
            });
            const continueScreen = store?.steps?.find((step) => step.screen === ScreenType.SELECT_ATTRIBUTES && step.trigger === ScreenTrigger.SUBMIT);
            onSubmit(continueScreen);
        }
    };

    return {
        category,
        selectedProduct,
        handleProductSelected,
        navigateBack,
        confirmProductSelection,
        isCheckingOrderPermission,
        isRecycleMode,
        isOrderFallback: selectedProduct?.type === ProductType.CONSUMABLE && selectedProductStocks.length === 0 && canOrderSelectedProduct,
        hasOrderFallbackStep: !!orderFallbackStep
    };
};
